import { AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { useMemo } from 'react';
import {
  getAccountGroups,
  useAccounts,
  useBudgetId,
  useBudgetSummary,
  useCurrencyFormatter,
} from '@liquidbudget/common';
import { AccountNavLink } from '@/components/budget-nav/account-nav-link';

type Props = {};

export function AccountNavGroups({}: Props) {
  const budgetId = useBudgetId();
  const formatCurrency = useCurrencyFormatter();
  const accounts = useAccounts();
  const { accountSummary } = useBudgetSummary();
  const { groups } = useMemo(() => getAccountGroups(accounts), [accounts]);

  /**
   * Calculates the total balance for a specific account group.
   *
   * @param groupAccounts - Array of accounts in the group
   * @returns The sum of all account balances in the group
   */
  const calculateGroupBalance = useMemo(() => {
    return (groupAccounts: typeof accounts) => {
      return groupAccounts.reduce((total, account) => {
        const accountBalance = accountSummary.accountBalanceById.get(account.id) || 0;
        return total + accountBalance;
      }, 0);
    };
  }, [accountSummary.accountBalanceById]);

  /**
   * Creates a safe accordion value from a group name by converting to lowercase
   * and replacing spaces with hyphens.
   *
   * @param groupName - The display name of the group
   * @returns A safe string to use as an accordion value
   */
  const createAccordionValue = (groupName: string): string => {
    return groupName.toLowerCase().replace(/\s+/g, '-');
  };

  if (groups.length === 0) {
    return null;
  }

  return (
    <>
      {groups.map((group) => {
        const groupBalance = calculateGroupBalance(group.accounts);
        const accordionValue = createAccordionValue(group.name);

        return (
          <AccordionItem key={group.name} value={accordionValue} className="border-0">
            <AccordionTrigger className="py-2 px-2 pl-4 text-sm font-semibold hover:no-underline w-full">
              <div className="flex justify-between items-center w-full">
                <span className="text-muted-foreground pl-2">{group.name}</span>
                <span className="text-muted-foreground ml-auto mr-2 text-xs tabular-nums">
                  {formatCurrency(groupBalance)}
                </span>
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <div className="flex flex-col space-y-0">
                {group.accounts.map((account) => (
                  <AccountNavLink
                    key={account.id}
                    account={account}
                    to={`/budget/${budgetId}/account/${account.id}`}
                    text={account.name}
                    error={(accountSummary.uncategorizedBalanceById.get(account.id) || 0) > 0}
                    indicator={(accountSummary.clearedBalanceCountById.get(account.id) || 0) > 0}
                    aside={formatCurrency(accountSummary.accountBalanceById.get(account.id) ?? 0)}
                  />
                ))}
              </div>
            </AccordionContent>
          </AccordionItem>
        );
      })}
    </>
  );
}
